import { useEffect, useState } from "react";
import axios from "axios";
import {
  Paper, Typography, Stack, Box, List, ListItemButton, ListItemText, Divider,
  Dialog, DialogContent, DialogActions, Button, CircularProgress, Chip, Alert,
} from "@mui/material";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import DownloadIcon from "@mui/icons-material/Download";
import { API_BASE_URL } from "../config";
import { useFullScreenDialog } from "../useFullScreenDialog";
import GuidelineDocument from "../components/GuidelineDocument";
import LinkList, { LINKS_API } from "../components/LinkList";
import { GUIDELINE_LINK_LISTS, GUIDELINES_DISCLAIMER } from "../constants";
import { exportGuidelinePdf } from "../exportGuidelinePdf";

const API = `${API_BASE_URL}/guidelines`;

// Group a flat, category-then-title ordered list into [{category, items}].
const groupByCategory = (list) => {
  const groups = [];
  let current = null;
  for (const g of list) {
    const cat = g.category || "Uncategorised";
    if (!current || current.category !== cat) {
      current = { category: cat, items: [] };
      groups.push(current);
    }
    current.items.push(g);
  }
  return groups;
};

// Section wrapper — each of the three lists gets the same heading treatment.
const Section = ({ title, children }) => (
  <Box>
    <Typography variant="h6" gutterBottom>{title}</Typography>
    {children}
  </Box>
);

export default function GuidelinesView() {
  const fullScreen = useFullScreenDialog();
  const [guidelines, setGuidelines] = useState([]);
  const [linksByKind, setLinksByKind] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);

  const doExport = () => {
    if (!selected) return;
    setExporting(true);
    Promise.resolve(exportGuidelinePdf(selected)).finally(() => setExporting(false));
  };

  useEffect(() => {
    // Each list is fetched by kind — the server gates reads per list.
    const fetchLinks = (kind) => axios.get(LINKS_API + "/", { params: { kind } })
      .then(res => setLinksByKind(m => ({ ...m, [kind]: res.data || [] })));
    Promise.all([
      axios.get(API + "/live").then(res => setGuidelines(res.data || [])),
      ...GUIDELINE_LINK_LISTS.map(l => fetchLinks(l.kind)),
    ]).finally(() => setLoading(false));
  }, []);

  const open = (g) => {
    axios.get(`${API}/${g.id}`).then(res => setSelected(res.data));
  };

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>;
  }

  const groups = groupByCategory(guidelines);
  const [policies, publics] = GUIDELINE_LINK_LISTS;
  const linksOf = (kind) => linksByKind[kind] || [];

  return (
    <Stack spacing={4}>
      <Typography variant="h4">Guidelines</Typography>

      <Section title={policies.heading}>
        <LinkList links={linksOf(policies.kind)} empty={policies.empty} />
      </Section>

      <Section title="Shelter Stone clinical guidelines">
        {guidelines.length === 0 ? (
          <Typography color="text.secondary">No guidelines are currently published.</Typography>
        ) : (
          <Stack spacing={2}>
            {groups.map(group => (
              <Paper key={group.category} elevation={0}
                     sx={{ border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
                <Typography variant="subtitle1" sx={{ px: 2, py: 1.5, fontWeight: 700, bgcolor: "action.hover" }}>
                  {group.category}
                </Typography>
                <Divider />
                <List disablePadding>
                  {group.items.map(g => (
                    <ListItemButton key={g.id} onClick={() => open(g)}>
                      <MenuBookIcon fontSize="small" sx={{ mr: 1.5, color: "text.secondary" }} />
                      <ListItemText primary={g.title} />
                      {g.version && <Chip size="small" variant="outlined" label={`Version ${g.version}`} />}
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            ))}
          </Stack>
        )}
      </Section>

      <Section title={publics.heading}>
        <LinkList links={linksOf(publics.kind)} empty={publics.empty} />
      </Section>

      <Alert severity="info" icon={false}>{GUIDELINES_DISCLAIMER}</Alert>

      <Dialog open={selected !== null} onClose={() => setSelected(null)}
              fullWidth maxWidth="md" fullScreen={fullScreen}>
        <DialogContent dividers>
          <GuidelineDocument guideline={selected} />
        </DialogContent>
        <DialogActions>
          <Button onClick={doExport} disabled={exporting}
                  startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}>
            Export PDF
          </Button>
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
